import React from "react";
import { useNavigate } from 'react-router-dom';
import './App.css';
import { useEffect, useState, useRef } from 'react';
import * as Auth from 'aws-amplify/auth';
import { get } from '@aws-amplify/api';
import AppLayout from './components/AppLayout';

const GenerateButterflyOptionsLegs = ({ icType }) => {
  console.log("GenerateButterflyOptionsLegs : icType = " + icType);

  const navigate = useNavigate();
  let email = useRef();

  const [error, setError] = useState('');
  const [displayMessage, setDisplayMessage] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  let optionsLegs = useRef({});
  const [navigateToLegs, setNavigateToLegs] = useState(false);

  // Track whether using Delta or Price mode
  const [mode, setMode] = useState("price"); // "delta" or "price" - default to "price"

  // --- DEFAULTS (updated per requirements) ---
  const defaultFormData = {
    security: "",
    targetDte: "", // No default
    targetFriday: "no",
    leg1: {
      action: "Buy",
      type: "Call",
      quantity: 1,
      targetDelta: "", // No default
      strikePrice: ""
    },
    leg2: {
      action: "Sell",
      type: "Call",
      quantity: 2,
      targetDelta: "", // No default
      strikePrice: ""
    },
    leg3: {
      action: "Buy",
      type: "Call",
      quantity: 1,
      targetDelta: "", // No default
      strikePrice: ""
    }
  };

  // Form data state
  const [formData, setFormData] = useState(defaultFormData);

  async function getUserInfo() {
    console.log("Executing getUserInfo");
    await Auth.fetchAuthSession();
    const user = await Auth.fetchUserAttributes();
    email.current = user.email;
    console.log("getUserInfo : email = " + email.current);
  }

  async function submitToBackend(payloadOverride = null) {
    console.log("Executing submitToBackend");
    setLoading(true);
    setError('');
    setDisplayMessage('');

    const myAPI = "mktappapi";
    let path = '/butterflyOptionsLegsGeneration';

    const fd = payloadOverride || formData;

    // Get user email
    const attributes = await Auth.fetchUserAttributes();
    const userEmail = attributes.email;

    const payload = {
      email: userEmail,
      security: fd.security.toUpperCase().trim(),
      targetDte: fd.targetDte,
      targetFriday: fd.targetFriday,
      mode: mode, // "delta" or "price"
      percentAwayFromLastSale: fd.percentAwayFromLastSale || null, // For Dollar Percent mode
      legs: [
        {
          legNumber: 1,
          label: "Lower Wing",
          action: fd.leg1.action,
          type: fd.leg1.type,
          quantity: fd.leg1.quantity,
          targetDelta: mode === "delta" ? fd.leg1.targetDelta : null,
          strikePrice: mode === "price" ? fd.leg1.strikePrice : null
        },
        {
          legNumber: 2,
          label: "Body",
          action: fd.leg2.action,
          type: fd.leg2.type,
          quantity: fd.leg2.quantity,
          targetDelta: mode === "delta" ? fd.leg2.targetDelta : null,
          strikePrice: mode === "price" ? fd.leg2.strikePrice : null
        },
        {
          legNumber: 3,
          label: "Upper Wing",
          action: fd.leg3.action,
          type: fd.leg3.type,
          quantity: fd.leg3.quantity,
          targetDelta: mode === "delta" ? fd.leg3.targetDelta : null,
          strikePrice: mode === "price" ? fd.leg3.strikePrice : null
        }
      ]
    };

    const passApiStr = path + "/" + encodeURIComponent(JSON.stringify(payload));
    console.log("submitToBackend : passApiStr = " + passApiStr);

    try {
      const response = await get({
        apiName: myAPI,
        path: passApiStr,
      }).response;

      let resp = await response.body.json();
      console.log("submitToBackend : resp = " + JSON.stringify(resp));

      optionsLegs.current = resp;
      setDisplayMessage("Options legs generated successfully! Redirecting...");
      setError("");
      setNavigateToLegs(true);
    } catch (err) {
      setError(err.message || "An error occurred while generating options legs");
      console.log("submitToBackend : Error = " + (err.message || err));
    } finally {
      setLoading(false);
    }
  }

  // initial user info
  useEffect(() => {
    console.log("useEffect(init): getUserInfo");
    if (typeof email.current === 'undefined') {
      getUserInfo();
    }
  }, []);

  // navigation once backend returns legs
  useEffect(() => {
    if (navigateToLegs && optionsLegs.current && Object.keys(optionsLegs.current).length > 0) {
      navigate('/butterflyOptionsLegs', { state: { optionsLegs: optionsLegs.current } });
    }
  }, [navigateToLegs, navigate]);

  // --- QUICK MODE (icType provided via router) ---
  useEffect(() => {
    console.log("useEffect(icType): icType = ", icType);
    if (icType && icType !== "" && !submitted) {
      // Load butterfly settings before auto-submitting
      loadButterflySettingsAndSubmit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [icType, submitted]);

  const loadButterflySettingsAndSubmit = async () => {
    try {
      // Ensure we have user email
      if (typeof email.current === 'undefined' || email.current === "") {
        await getUserInfo();
      }

      // Load butterfly settings
      const response = await get({
        apiName: "mktappapi",
        path: `/getButterflySettings/${email.current}`,
      }).response;

      const settings = await response.body.json();
      console.log("Loaded butterfly settings for quick mode:", settings);

      // Use settings or defaults
      const dte = settings.dte || 15;
      const targetFriday = settings.targetFriday || "yes";
      const strategy = settings.strategy || "Dollar Percent Legs";
      const percentAwayFromLastSale = settings.percentAwayFromLastSale || "0.5";
      const leg1delta = settings.leg1delta;
      const leg2delta = settings.leg2delta;
      const leg3delta = settings.leg3delta;

      let quick;
      if (strategy === "Dollar Percent Legs") {
        // Dollar Percent mode - use price mode and pass percentAwayFromLastSale to backend
        quick = {
          ...defaultFormData,
          security: icType.toUpperCase(),
          targetDte: dte,
          targetFriday: targetFriday,
          percentAwayFromLastSale: percentAwayFromLastSale,
          leg1: { ...defaultFormData.leg1 },
          leg2: { ...defaultFormData.leg2 },
          leg3: { ...defaultFormData.leg3 }
        };
        setMode("price");
      } else {
        // Delta Legs mode
        quick = {
          ...defaultFormData,
          security: icType.toUpperCase(),
          targetDte: dte,
          targetFriday: targetFriday,
          leg1: { ...defaultFormData.leg1, targetDelta: leg1delta || "" },
          leg2: { ...defaultFormData.leg2, targetDelta: leg2delta || "" },
          leg3: { ...defaultFormData.leg3, targetDelta: leg3delta || "" }
        };
        setMode("delta");
      }

      setFormData(quick);
      setSubmitted(true);

      if (displayMessage === "") {
        setDisplayMessage("Getting Butterfly Data ...");
      }

      submitToBackend(quick);
    } catch (error) {
      console.error("Error loading butterfly settings:", error);
      // Fall back to defaults if settings load fails
      const quick = {
        ...defaultFormData,
        security: icType.toUpperCase(),
        targetDte: 15,
        targetFriday: "yes",
        percentAwayFromLastSale: "0.5",
        leg1: { ...defaultFormData.leg1 },
        leg2: { ...defaultFormData.leg2 },
        leg3: { ...defaultFormData.leg3 }
      };
      setFormData(quick);
      setMode("price");
      setSubmitted(true);
      setDisplayMessage("Getting Butterfly Data ...");
      submitToBackend(quick);
    }
  };

  const handleInputChange = (field, value) => {
    if (field === 'security') {
      value = value.toUpperCase();
    }
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleLegChange = (legNumber, field, value) => {
    const legKey = `leg${legNumber}`;

    // Maintain the butterfly ratio if quantity changes
    if (field === 'quantity') {
      let quantity = parseInt(value) || 1;

      let leg1Qty, leg2Qty, leg3Qty;

      if (legNumber === 1 || legNumber === 3) {
        leg1Qty = quantity;
        leg3Qty = quantity;
        leg2Qty = quantity * 2;
      } else if (legNumber === 2) {
        leg2Qty = quantity % 2 === 0 ? quantity : quantity + 1;
        leg1Qty = leg2Qty / 2;
        leg3Qty = leg2Qty / 2;
      }

      setFormData(prev => ({
        ...prev,
        leg1: { ...prev.leg1, quantity: leg1Qty },
        leg2: { ...prev.leg2, quantity: leg2Qty },
        leg3: { ...prev.leg3, quantity: leg3Qty }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [legKey]: {
          ...prev[legKey],
          [field]: value
        }
      }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.security.trim()) {
      setError("Security is required");
      return;
    }
    if (!formData.targetDte) {
      setError("Target DTE is required");
      return;
    }

    // Validate that either all deltas or all prices are filled
    if (mode === "delta") {
      if (!formData.leg1.targetDelta || !formData.leg2.targetDelta || !formData.leg3.targetDelta) {
        setError("Please fill in all Target Delta fields");
        return;
      }
    } else if (mode === "price") {
      if (!formData.leg1.strikePrice || !formData.leg2.strikePrice || !formData.leg3.strikePrice) {
        setError("Please fill in all Strike Price fields");
        return;
      }
    }

    setSubmitted(true);
    if (displayMessage === "") {
      setDisplayMessage("Getting Butterfly Data ...");
    }
    submitToBackend();
  };

  const handleDefault = () => {
    setFormData(defaultFormData);
    setMode("delta");
    setError('');
    setDisplayMessage('');
    setSubmitted(false);
  };

  const inputStyle = {
    padding: '8px 12px',
    borderRadius: '4px',
    border: '1px solid #ccc',
    fontSize: '14px',
    minWidth: '80px'
  };

  const selectStyle = {
    ...inputStyle,
    minWidth: '100px'
  };

  const rowStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '15px',
    marginBottom: '15px',
    flexWrap: 'wrap'
  };

  const fieldGroupStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '5px'
  };

  // --- QUICK MODE UI (icType provided) ---
  if (icType && icType !== "") {
    return (
      <AppLayout signOut={undefined}>
        <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '24px' }}>
          <div style={{ marginBottom: '24px' }}>
            <h1 style={{ fontSize: '28px', fontWeight: '700', color: 'rgba(0, 0, 0, 0.9)' }}>
              Generate Butterfly Options
            </h1>
          </div>

          {/* Loading Message */}
          {!error && displayMessage && (
            <div className="blink_me" style={{
              marginBottom: '16px',
              textAlign: 'center',
              fontSize: '16px',
              padding: '16px',
              background: 'rgba(59, 130, 246, 0.1)',
              borderRadius: '8px'
            }}>
              {displayMessage}
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="blink_me_red" style={{
              marginBottom: '16px',
              textAlign: 'center',
              fontSize: '16px',
              padding: '16px'
            }}>
              {error}
            </div>
          )}

          {/* Info Card showing parameters */}
          <div className="landing-panel">
            <div className="landing-panel-header">
              <h2 className="landing-panel-title">⚡ Quick Butterfly - {icType}</h2>
            </div>
            <div className="landing-panel-content">
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div style={{ padding: '12px', background: 'rgba(249, 250, 251, 1)', borderRadius: '8px' }}>
                  <div style={{ fontWeight: '600', marginBottom: '8px', color: 'rgba(0, 0, 0, 0.8)' }}>Security:</div>
                  <div style={{ fontSize: '18px', fontWeight: '500' }}>{icType}</div>
                </div>
                <div style={{ padding: '12px', background: 'rgba(249, 250, 251, 1)', borderRadius: '8px' }}>
                  <div style={{ fontWeight: '600', marginBottom: '8px', color: 'rgba(0, 0, 0, 0.8)' }}>Target DTE:</div>
                  <div style={{ fontSize: '18px', fontWeight: '500' }}>{formData.targetDte}</div>
                </div>
                <div style={{ padding: '12px', background: 'rgba(249, 250, 251, 1)', borderRadius: '8px' }}>
                  <div style={{ fontWeight: '600', marginBottom: '8px', color: 'rgba(0, 0, 0, 0.8)' }}>Leg Qty Ratio:</div>
                  <div style={{ fontSize: '18px', fontWeight: '500' }}>
                    {formData.leg1.quantity} / {formData.leg2.quantity} / {formData.leg3.quantity}
                  </div>
                </div>
                <div style={{ padding: '12px', background: 'rgba(249, 250, 251, 1)', borderRadius: '8px' }}>
                  <div style={{ fontWeight: '600', marginBottom: '8px', color: 'rgba(0, 0, 0, 0.8)' }}>Deltas:</div>
                  <div style={{ fontSize: '18px', fontWeight: '500' }}>
                    {formData.leg1.targetDelta} / {formData.leg2.targetDelta} / {formData.leg3.targetDelta}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Optional: show a generating indicator */}
          {loading && (
            <div className="blink_me" style={{
              marginTop: '16px',
              textAlign: 'center',
              fontSize: '16px',
              padding: '12px',
              background: 'rgba(59, 130, 246, 0.1)',
              borderRadius: '8px'
            }}>
              Generating Butterfly Legs...
            </div>
          )}
        </div>
      </AppLayout>
    );
  }

  // --- NORMAL (manual form) UI ---
  return (
    <AppLayout signOut={undefined}>
      <div style={{ padding: '24px', maxWidth: '1000px', margin: '0 auto' }}>
        {/* Page Title */}
        <div style={{ marginBottom: '32px' }}>
          <h1 style={{ fontSize: '28px', fontWeight: '700', color: 'rgba(0, 0, 0, 0.9)', marginBottom: '8px' }}>
            Generate Butterfly Options Legs
          </h1>
          <p style={{ fontSize: '14px', color: 'rgba(107, 114, 128, 1)' }}>
            Configure your butterfly spread parameters and generate option legs
          </p>
        </div>

        {/* Error Message */}
        {error && (
          <div style={{
            padding: '12px',
            marginBottom: '16px',
            background: 'rgba(239, 68, 68, 0.1)',
            color: '#ef4444',
            borderRadius: '8px',
            fontWeight: '500'
          }}>
            {error}
          </div>
        )}

        {/* Success Message */}
        {displayMessage && (
          <div style={{
            padding: '12px',
            marginBottom: '16px',
            background: 'rgba(16, 185, 129, 0.1)',
            color: '#10b981',
            borderRadius: '8px',
            fontWeight: '500'
          }}>
            {displayMessage}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {/* Basic Parameters Card */}
          <div style={{
            background: 'white',
            borderRadius: '12px',
            border: '1px solid rgba(229, 231, 235, 1)',
            padding: '24px',
            marginBottom: '24px',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
          }}>
            <h3 style={{ fontSize: '18px', fontWeight: '600', color: 'rgba(0, 0, 0, 0.9)', marginBottom: '16px' }}>
              Basic Parameters
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' }}>
              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: 'rgba(55, 65, 81, 1)'
                }}>
                  Security
                </label>
                <input
                  type="text"
                  value={formData.security}
                  onChange={(e) => handleInputChange('security', e.target.value)}
                  placeholder="e.g., SPY"
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    fontSize: '14px',
                    border: '1px solid rgba(229, 231, 235, 1)',
                    borderRadius: '8px',
                    outline: 'none'
                  }}
                />
              </div>
              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: 'rgba(55, 65, 81, 1)'
                }}>
                  Target DTE (Days to Expiration)
                </label>
                <input
                  type="number"
                  value={formData.targetDte}
                  onChange={(e) => handleInputChange('targetDte', parseInt(e.target.value) || '')}
                  min="1"
                  placeholder="e.g., 15"
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    fontSize: '14px',
                    border: '1px solid rgba(229, 231, 235, 1)',
                    borderRadius: '8px',
                    outline: 'none'
                  }}
                />
              </div>
              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: 'rgba(55, 65, 81, 1)'
                }}>
                  Target Friday
                </label>
                <select
                  value={formData.targetFriday}
                  onChange={(e) => handleInputChange('targetFriday', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    fontSize: '14px',
                    border: '1px solid rgba(229, 231, 235, 1)',
                    borderRadius: '8px',
                    outline: 'none'
                  }}
                >
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                </select>
              </div>
            </div>

            {/* Mode Toggle */}
            <div style={{ marginTop: '24px' }}>
              <label style={{
                display: 'block',
                marginBottom: '12px',
                fontSize: '14px',
                fontWeight: '500',
                color: 'rgba(55, 65, 81, 1)'
              }}>
                Selection Mode
              </label>
              <div style={{
                display: 'inline-flex',
                background: 'rgba(243, 244, 246, 1)',
                borderRadius: '8px',
                padding: '4px'
              }}>
                <button
                  type="button"
                  onClick={() => setMode("delta")}
                  style={{
                    padding: '10px 24px',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    background: mode === "delta" ? 'white' : 'transparent',
                    color: mode === "delta" ? 'rgba(59, 130, 246, 1)' : 'rgba(107, 114, 128, 1)',
                    boxShadow: mode === "delta" ? '0 1px 3px rgba(0, 0, 0, 0.1)' : 'none'
                  }}
                >
                  Target Delta
                </button>
                <button
                  type="button"
                  onClick={() => setMode("price")}
                  style={{
                    padding: '10px 24px',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    background: mode === "price" ? 'white' : 'transparent',
                    color: mode === "price" ? 'rgba(59, 130, 246, 1)' : 'rgba(107, 114, 128, 1)',
                    boxShadow: mode === "price" ? '0 1px 3px rgba(0, 0, 0, 0.1)' : 'none'
                  }}
                >
                  Strike Price
                </button>
              </div>
            </div>
          </div>

          {/* Legs Configuration Card */}
          <div style={{
            background: 'white',
            borderRadius: '12px',
            border: '1px solid rgba(229, 231, 235, 1)',
            padding: '24px',
            marginBottom: '24px',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
          }}>
            <h3 style={{ fontSize: '18px', fontWeight: '600', color: 'rgba(0, 0, 0, 0.9)', marginBottom: '20px' }}>
              Legs Configuration
            </h3>

            {/* Leg 1 */}
            <div style={{
              backgroundColor: 'rgba(249, 250, 251, 1)',
              padding: '20px',
              borderRadius: '8px',
              marginBottom: '16px',
              border: '1px solid rgba(229, 231, 235, 1)'
            }}>
              <div style={{ fontWeight: '600', fontSize: '16px', marginBottom: '12px', color: 'rgba(0, 0, 0, 0.9)' }}>
                Leg 1 (Lower Wing)
              </div>
              <div style={rowStyle}>
                <div style={fieldGroupStyle}>
                  <label style={{ marginRight: '5px' }}>Action:</label>
                  <select
                    value={formData.leg1.action}
                    onChange={(e) => handleLegChange(1, 'action', e.target.value)}
                    style={selectStyle}
                  >
                    <option value="Buy">Buy</option>
                    <option value="Sell">Sell</option>
                  </select>
                </div>
                <div style={fieldGroupStyle}>
                  <label style={{ marginRight: '5px' }}>Type:</label>
                  <select
                    value={formData.leg1.type}
                    onChange={(e) => handleLegChange(1, 'type', e.target.value)}
                    style={selectStyle}
                  >
                    <option value="Call">Call</option>
                    <option value="Put">Put</option>
                  </select>
                </div>
                <div style={fieldGroupStyle}>
                  <label style={{ marginRight: '5px' }}>Quantity:</label>
                  <input
                    type="number"
                    value={formData.leg1.quantity}
                    onChange={(e) => handleLegChange(1, 'quantity', parseInt(e.target.value) || 1)}
                    min="1"
                    style={{ ...inputStyle, width: '60px' }}
                  />
                </div>
                {mode === "delta" && (
                  <div style={fieldGroupStyle}>
                    <label style={{ marginRight: '5px' }}>Target Delta:</label>
                    <input
                      type="number"
                      value={formData.leg1.targetDelta}
                      onChange={(e) => handleLegChange(1, 'targetDelta', e.target.value)}
                      step="1"
                      min="1"
                      max="99"
                      placeholder="e.g., 63"
                      style={{ ...inputStyle, width: '80px' }}
                    />
                  </div>
                )}
                {mode === "price" && (
                  <div style={fieldGroupStyle}>
                    <label style={{ marginRight: '5px' }}>Strike Price:</label>
                    <input
                      type="number"
                      value={formData.leg1.strikePrice}
                      onChange={(e) => handleLegChange(1, 'strikePrice', e.target.value)}
                      step="0.5"
                      placeholder="e.g., 550"
                      style={{ ...inputStyle, width: '90px' }}
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Leg 2 */}
            <div style={{
              backgroundColor: 'rgba(249, 250, 251, 1)',
              padding: '20px',
              borderRadius: '8px',
              marginBottom: '16px',
              border: '1px solid rgba(229, 231, 235, 1)'
            }}>
              <div style={{ fontWeight: '600', fontSize: '16px', marginBottom: '12px', color: 'rgba(0, 0, 0, 0.9)' }}>
                Leg 2 (Body)
              </div>
              <div style={rowStyle}>
                <div style={fieldGroupStyle}>
                  <label style={{ marginRight: '5px' }}>Action:</label>
                  <select
                    value={formData.leg2.action}
                    onChange={(e) => handleLegChange(2, 'action', e.target.value)}
                    style={selectStyle}
                  >
                    <option value="Buy">Buy</option>
                    <option value="Sell">Sell</option>
                  </select>
                </div>
                <div style={fieldGroupStyle}>
                  <label style={{ marginRight: '5px' }}>Type:</label>
                  <select
                    value={formData.leg2.type}
                    onChange={(e) => handleLegChange(2, 'type', e.target.value)}
                    style={selectStyle}
                  >
                    <option value="Call">Call</option>
                    <option value="Put">Put</option>
                  </select>
                </div>
                <div style={fieldGroupStyle}>
                  <label style={{ marginRight: '5px' }}>Quantity:</label>
                  <input
                    type="number"
                    value={formData.leg2.quantity}
                    onChange={(e) => handleLegChange(2, 'quantity', parseInt(e.target.value) || 2)}
                    min="2"
                    step="2"
                    style={{ ...inputStyle, width: '60px' }}
                  />
                </div>
                {mode === "delta" && (
                  <div style={fieldGroupStyle}>
                    <label style={{ marginRight: '5px' }}>Target Delta:</label>
                    <input
                      type="number"
                      value={formData.leg2.targetDelta}
                      onChange={(e) => handleLegChange(2, 'targetDelta', e.target.value)}
                      step="1"
                      min="1"
                      max="99"
                      placeholder="e.g., 50"
                      style={{ ...inputStyle, width: '80px' }}
                    />
                  </div>
                )}
                {mode === "price" && (
                  <div style={fieldGroupStyle}>
                    <label style={{ marginRight: '5px' }}>Strike Price:</label>
                    <input
                      type="number"
                      value={formData.leg2.strikePrice}
                      onChange={(e) => handleLegChange(2, 'strikePrice', e.target.value)}
                      step="0.5"
                      placeholder="e.g., 555"
                      style={{ ...inputStyle, width: '90px' }}
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Leg 3 */}
            <div style={{
              backgroundColor: 'rgba(249, 250, 251, 1)',
              padding: '20px',
              borderRadius: '8px',
              marginBottom: '16px',
              border: '1px solid rgba(229, 231, 235, 1)'
            }}>
              <div style={{ fontWeight: '600', fontSize: '16px', marginBottom: '12px', color: 'rgba(0, 0, 0, 0.9)' }}>
                Leg 3 (Upper Wing)
              </div>
              <div style={rowStyle}>
                <div style={fieldGroupStyle}>
                  <label style={{ marginRight: '5px' }}>Action:</label>
                  <select
                    value={formData.leg3.action}
                    onChange={(e) => handleLegChange(3, 'action', e.target.value)}
                    style={selectStyle}
                  >
                    <option value="Buy">Buy</option>
                    <option value="Sell">Sell</option>
                  </select>
                </div>
                <div style={fieldGroupStyle}>
                  <label style={{ marginRight: '5px' }}>Type:</label>
                  <select
                    value={formData.leg3.type}
                    onChange={(e) => handleLegChange(3, 'type', e.target.value)}
                    style={selectStyle}
                  >
                    <option value="Call">Call</option>
                    <option value="Put">Put</option>
                  </select>
                </div>
                <div style={fieldGroupStyle}>
                  <label style={{ marginRight: '5px' }}>Quantity:</label>
                  <input
                    type="number"
                    value={formData.leg3.quantity}
                    onChange={(e) => handleLegChange(3, 'quantity', parseInt(e.target.value) || 1)}
                    min="1"
                    style={{ ...inputStyle, width: '60px' }}
                  />
                </div>
                {mode === "delta" && (
                  <div style={fieldGroupStyle}>
                    <label style={{ marginRight: '5px' }}>Target Delta:</label>
                    <input
                      type="number"
                      value={formData.leg3.targetDelta}
                      onChange={(e) => handleLegChange(3, 'targetDelta', e.target.value)}
                      step="1"
                      min="1"
                      max="99"
                      placeholder="e.g., 35"
                      style={{ ...inputStyle, width: '80px' }}
                    />
                  </div>
                )}
                {mode === "price" && (
                  <div style={fieldGroupStyle}>
                    <label style={{ marginRight: '5px' }}>Strike Price:</label>
                    <input
                      type="number"
                      value={formData.leg3.strikePrice}
                      onChange={(e) => handleLegChange(3, 'strikePrice', e.target.value)}
                      step="0.5"
                      placeholder="e.g., 560"
                      style={{ ...inputStyle, width: '90px' }}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Buttons */}
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', marginTop: '24px' }}>
            <button
              type="submit"
              disabled={loading}
              style={{
                padding: '14px 32px',
                background: loading
                  ? 'rgba(59, 130, 246, 0.6)'
                  : 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                border: 'none',
                borderRadius: '8px',
                color: 'white',
                fontSize: '16px',
                fontWeight: '600',
                cursor: loading ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s',
                boxShadow: loading ? 'none' : '0 4px 6px rgba(59, 130, 246, 0.3)',
              }}
            >
              {loading ? 'Generating...' : 'Generate Legs'}
            </button>

            <button
              type="button"
              onClick={handleDefault}
              style={{
                padding: '14px 32px',
                background: 'rgba(107, 114, 128, 0.1)',
                border: '1px solid rgba(107, 114, 128, 0.3)',
                borderRadius: '8px',
                color: 'rgba(55, 65, 81, 1)',
                fontSize: '16px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
            >
              Reset to Default
            </button>
          </div>
        </form>
      </div>
    </AppLayout>
  );
};

export default GenerateButterflyOptionsLegs;
